import { useEffect, useRef } from 'react';
import { useNodeStore } from '@/stores/nodeStore';
import { useTaskStore } from '@/stores/taskStore';
import { useProjectStore } from '@/stores/projectStore';
import { eventBus, CanvasEvents } from '@/utils/eventBus';
import { nodeHasResult } from '@/hooks/useDataPersistence';
import { recoverWorkingNodes } from '@/hooks/useProjectNavigation';
import { NodeStatus, AppNode } from '@/types';
import { findStrategy, GenerationContext } from '@/services/generationStrategies';

interface UseTaskRecoveryParams {
  isLoaded: boolean;
  handleAssetGenerated: (type: 'image' | 'video' | 'audio', url: string, prompt: string, genPrompt?: string) => void;
}

export function useTaskRecovery({ isLoaded, handleAssetGenerated }: UseTaskRecoveryParams) {
  const resumedTasksRef = useRef<Set<string>>(new Set());
  // 记录上次触发恢复的 projectId，避免在同一项目内因 nodes/pendingTaskCount 变化重复触发
  const lastRecoveryProjectIdRef = useRef<string | null>(null);
  // 记录上次触发 resumePendingTasks 的 projectId + pendingTaskCount 组合，避免重复调用
  const lastResumeKeyRef = useRef<string | null>(null);

  const currentProjectId = useProjectStore((s) => s.currentProjectId);
  const nodes = useNodeStore((s) => s.nodes);
  const pendingTaskCount = useTaskStore((s) => s.pendingTasks.size);
  const resumePendingTasks = useTaskStore((s) => s.resumePendingTasks);

  useEffect(() => {
    if (!isLoaded || !currentProjectId) return;

    // 切换项目时重置防重复标记
    if (lastRecoveryProjectIdRef.current !== currentProjectId) {
      lastRecoveryProjectIdRef.current = currentProjectId;
      lastResumeKeyRef.current = null;
      resumedTasksRef.current.clear();
    }

    const currentNodes = useNodeStore.getState().nodes;
    if (currentNodes.length === 0) return;

    const needsRecovery = currentNodes.some(n => n.data._needsTaskRecovery);
    const hasWorkingNode = currentNodes.some(n => n.status === NodeStatus.WORKING);
    if (!hasWorkingNode && !needsRecovery) return;

    // resumePendingTasks 内部有 _pollingTaskIds 防重复轮询，但这里再用 resumeKey 防止重复调用
    // 同一项目 + 同一 pendingTaskCount 只调用一次（pendingTaskCount 变化说明任务状态变了，允许再调）
    if (pendingTaskCount > 0 && hasWorkingNode) {
      const resumeKey = `${currentProjectId}:${pendingTaskCount}`;
      if (lastResumeKeyRef.current !== resumeKey) {
        lastResumeKeyRef.current = resumeKey;
        const { updateNode } = useNodeStore.getState();
        resumePendingTasks(
          (id, data, size, title, status) => updateNode(id, data, size, title, status),
          handleAssetGenerated,
          currentProjectId,
          (task, updates) => {
            if (resumedTasksRef.current.has(task.taskId)) return;
            resumedTasksRef.current.add(task.taskId);

            if (task.nodeId && task.nodeId.startsWith('agent-node-')) {
              eventBus.emit(CanvasEvents.AGENT_TASK_COMPLETED, {
                taskId: task.taskId,
                nodeId: task.nodeId,
                status: 'SUCCESS',
                updates,
              });
            }
          },
        );
      }
    }

    // 刷新页面 / 进入项目时的任务恢复：只在有 _needsTaskRecovery 标记时调用
    // recoverWorkingNodes 只处理 _needsTaskRecovery 节点（刷新后需要恢复的）
    // 不处理刚刚发起新生成的 WORKING 节点（避免后端任务还没创建时误判为失败）
    // 用 lastRecoveryProjectIdRef 防止同一项目重复触发（只在项目切换时重新触发）
    if (needsRecovery) {
      recoverWorkingNodes(currentProjectId).catch((e) => {
        console.error('[Recovery] recoverWorkingNodes failed:', e);
      });
    }
  }, [isLoaded, currentProjectId, nodes, pendingTaskCount]);

  useEffect(() => {
    if (!currentProjectId) return;

    const processedTaskIds = new Set<string>();

    const handleTaskCompleted = async (data: { taskId: string; nodeId: string; nodeType: string; request: Record<string, any>; result: any }) => {
      if (processedTaskIds.has(data.taskId)) return;
      processedTaskIds.add(data.taskId);

      const { updateNode } = useNodeStore.getState();
      const node = useNodeStore.getState().nodeMap.get(data.nodeId);
      if (!node) return;
      if (node.status === NodeStatus.SUCCESS || nodeHasResult(node)) return;

      try {
        const strategy = findStrategy(data.nodeType as any);
        const request = data.request || {};
        const prompt = request.prompt || '';

        if (strategy && data.result) {
          const mockNode: AppNode = {
            id: data.nodeId,
            type: data.nodeType as any,
            x: 0, y: 0,
            title: '',
            status: NodeStatus.WORKING,
            data: request,
            inputs: [],
          };

          const rawResult = {
            taskId: data.taskId,
            status: 'success',
            results: data.result.results,
            error: data.result.error,
            videoMetadata: data.result.videoMetadata,
            analysis: data.result.analysis,
          };

          const ctx: GenerationContext = {
            projectId: currentProjectId,
            onProgress: () => {},
            onAssetGenerated: handleAssetGenerated,
            onTaskCreated: () => {},
          };

          const updates = await strategy.processResult(mockNode, rawResult, ctx, prompt);
          updateNode(data.nodeId, { ...updates, progress: undefined, activeFeature: undefined, _needsTaskRecovery: false }, undefined, undefined, NodeStatus.SUCCESS);

          if (updates.image) handleAssetGenerated('image', updates.image, '', typeof request.prompt === 'string' ? request.prompt : undefined);
          if (updates.videoUri) handleAssetGenerated('video', updates.videoUri, '', typeof request.prompt === 'string' ? request.prompt : undefined);
          if (updates.audioUri) handleAssetGenerated('audio', updates.audioUri, '', typeof request.prompt === 'string' ? request.prompt : undefined);
        }
      } catch (e) {
        console.error(`[WS-TaskCompleted] Failed to process task ${data.taskId}:`, e);
      }
    };

    eventBus.on(CanvasEvents.TASK_COMPLETED, handleTaskCompleted);
    return () => {
      eventBus.off(CanvasEvents.TASK_COMPLETED, handleTaskCompleted);
    };
  }, [currentProjectId, handleAssetGenerated]);
}
